import { useCallback, useEffect, useState } from "react";
import { fileUrl } from "../lib/roles";
import AddOrderModal from "../components/staff/SO/AddOrderModal";
import DeleteOrderModal from "../components/staff/SO/DeleteOrderModal";
import ViewSOModal from "../components/staff/SO/ViewSOModal";

const StaffSellOrderPage = () => {
  const token = sessionStorage.getItem("token");

  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}orders/my`, {
        method: "GET",
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error("Failed to fetch orders");

      const data = await res.json();

      setOrders(data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const getStatusBadge = (approved) => {
    if (approved)
      return (
        <span className="px-2 py-1 text-xs rounded bg-emerald-600">Approved</span>
      );

    return (
      <span className="px-2 py-1 text-xs rounded bg-yellow-600">Pending</span>
    );
  };

  return (
    <div className="p-6 text-heading">
      {/* HEADER */}

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Sales Orders</h1>

          <p className="text-sm text-bodyText">
            Manage and track all sales orders
          </p>
        </div>

        <button
          onClick={() => setShowModal("Add")}
          className="bg-brand hover:bg-brand/90 px-4 py-2 rounded-lg shadow transition"
        >
          + New Sales Order
        </button>
      </div>

      {/* TABLE CONTAINER */}

      <div className="bg-card border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          {/* HEADER */}

          <thead className="bg-card border-b border-gray-200">
            <tr className="text-left text-bodyText">
              <th className="px-4 py-3">Subject</th>

              <th className="px-4 py-3">Account</th>

              <th className="px-4 py-3">Deal</th>

              <th className="px-4 py-3">Contact</th>

              <th className="px-4 py-3">Status</th>

              <th className="px-4 py-3">Total</th>

              <th className="px-4 py-3">Date</th>

              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>

          {/* BODY */}

          <tbody>
            {loading && (
              <tr>
                <td colSpan="8" className="text-center py-8 text-bodyText">
                  Loading orders...
                </td>
              </tr>
            )}

            {!loading && orders.length === 0 && (
              <tr>
                <td colSpan="8" className="text-center py-8 text-gray-500">
                  No sales orders found
                </td>
              </tr>
            )}

            {!loading &&
              orders.map((order) => (
                <tr
                  key={order._id}
                  className="border-t border-gray-200 hover:bg-surface transition"
                >
                  <td className="px-4 py-3 font-medium">
                    {order.finalQuote?.subject || "-"}
                  </td>

                  <td className="px-4 py-3">
                    {order.finalQuote?.account?.accountName || "-"}
                  </td>

                  <td className="px-4 py-3">
                    {order.finalQuote?.deal?.dealName || "-"}
                  </td>

                  <td className="px-4 py-3">
                    {order.finalQuote?.contact
                      ? `${order.finalQuote.contact.firstName} ${order.finalQuote.contact.lastName}`
                      : "-"}
                  </td>

                  <td className="px-4 py-3">
                    {getStatusBadge(order.isSOApproved)}
                  </td>

                  <td className="px-4 py-3 font-semibold text-red-400">
                    {order.finalQuote?.currency}{" "}
                    {order.grandTotal?.toLocaleString()}
                  </td>

                  <td className="px-4 py-3">
                    {order.confirmedDate
                      ? new Date(order.confirmedDate).toDateString()
                      : "-"}
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowModal("View");
                        }}
                        className="px-3 py-1 text-xs rounded bg-gray-100 hover:bg-gray-200"
                      >
                        View
                      </button>

                      {order.status === "Pending" && (
                        <>
                          <button className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700">
                            Edit
                          </button>

                          <button
                            onClick={() => {
                              setShowModal("Delete");
                              setSelectedOrder(order);
                            }}
                            className="px-3 py-1 text-xs rounded bg-brand hover:bg-brand/90"
                          >
                            Delete
                          </button>
                        </>
                      )}
                      {order.purchaseOrder?.url && (
                        <a
                          href={fileUrl(order.purchaseOrder.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <button className="px-3 py-1 text-xs rounded bg-green-500 hover:bg-emerald-600 text-white">
                            PO
                          </button>
                        </a>
                      )}

                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}quotes/${order.finalQuote?._id}/pdf`}
                      >
                        <button className="px-3 py-1 text-xs rounded bg-green-300 hover:bg-green-700">
                          Q-PDF
                        </button>
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* MODAL */}

      {showModal === "Add" && (
        <AddOrderModal
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(null);
            fetchOrders();
          }}
        />
      )}

      {showModal === "View" && (
        <ViewSOModal order={selectedOrder} onClose={() => setShowModal(null)} />
      )}

      {showModal === "Delete" && (
        <DeleteOrderModal
          orderId={selectedOrder._id}
          onClose={() => setShowModal("")}
          onSuccess={() => {
            setShowModal(null);
            fetchOrders();
          }}
        />
      )}
    </div>
  );
};

export default StaffSellOrderPage;
